import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { PRESENT_BLOCKING_ALERT_Z_INDEX } from "hooks/useWidgetStack";
import { selectClasses } from "features/userSlice";
import {
  LINKED_ACTIVE_DATES_SCOPE_ALL,
  LINKED_ACTIVE_DATES_SCOPE_CANCELLED,
  LINKED_ACTIVE_DATES_SCOPE_CURRENT,
  LINKED_ACTIVE_DATES_SCOPE_EVENT,
} from "utils/linkedActiveDatesSave";
import { resolveLinkedClassIds, uniqueClassIds } from "utils/linkedContentBoxes";
import { cleanupStaleModalArtifacts } from "utils/cleanupStaleModalArtifacts";

const toClassIdString = (classId) => String(classId || "");

const formatClassLabel = (classItem, fallbackId) => {
  if (!classItem) return fallbackId;
  const name = classItem.className || classItem.name || fallbackId;
  const period = classItem.period || classItem.classPeriod;
  return period ? `${period}: ${name}` : name;
};

const getBoxTitle = (box) =>
  box?.userHeading || box?.heading || box?.title || box?.name || "this linked box";

function LinkedActiveDatesChooserModal() {
  const reduxClasses = useSelector(selectClasses);
  const [request, setRequest] = useState(null);
  const [resolvedClassIds, setResolvedClassIds] = useState([]);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [error, setError] = useState("");
  const resolverRef = useRef(null);

  useEffect(() => {
    const handleScopeRequest = (event) => {
      event.preventDefault();
      resolverRef.current?.({
        scope: LINKED_ACTIVE_DATES_SCOPE_CANCELLED,
        scopeClassIds: [],
      });
      resolverRef.current = event.detail?.resolve;
      setResolvedClassIds([]);
      setError("");
      setRequest(event.detail || null);
    };

    window.addEventListener(LINKED_ACTIVE_DATES_SCOPE_EVENT, handleScopeRequest);
    return () => {
      window.removeEventListener(LINKED_ACTIVE_DATES_SCOPE_EVENT, handleScopeRequest);
      resolverRef.current?.({
        scope: LINKED_ACTIVE_DATES_SCOPE_CANCELLED,
        scopeClassIds: [],
      });
      resolverRef.current = null;
    };
  }, []);

  const sourceClassId = toClassIdString(request?.sourceClassId);
  const classes = request?.classes?.length ? request.classes : reduxClasses;
  const classById = useMemo(
    () => new Map(classes.map((classItem) => [toClassIdString(classItem?.classId), classItem])),
    [classes]
  );

  useEffect(() => {
    if (!request?.userId || !request?.contentBoxId || !sourceClassId) {
      return undefined;
    }

    let isActive = true;
    const loadClassIds = async () => {
      setIsLoadingClasses(true);
      setError("");
      try {
        const hintedClassIds = Array.isArray(request.linkedClassIds) && request.linkedClassIds.length
          ? request.linkedClassIds
          : await resolveLinkedClassIds(request.userId, request.contentBoxId, {
            sourceClassId,
            classIds: classes.map((classItem) => classItem?.classId).filter(Boolean),
            hintLinkedClassIds: request.contentBox?.linkedClassIds,
          });
        if (!isActive) return;
        setResolvedClassIds(uniqueClassIds([sourceClassId, ...hintedClassIds]));
      } catch (loadError) {
        console.error("Failed to load linked classes for active dates chooser:", loadError);
        if (!isActive) return;
        setResolvedClassIds([sourceClassId]);
        setError("Unable to load every linked class. You can still change this class only.");
      } finally {
        if (isActive) {
          setIsLoadingClasses(false);
        }
      }
    };

    loadClassIds();
    return () => {
      isActive = false;
    };
  }, [classes, request, sourceClassId]);

  const resolveAndClose = useCallback((scope, scopeClassIds) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setRequest(null);
    setResolvedClassIds([]);
    setError("");
    cleanupStaleModalArtifacts();
    resolve?.({
      scope,
      scopeClassIds: uniqueClassIds(scopeClassIds),
    });
  }, []);

  const cancel = useCallback(() => {
    resolveAndClose(LINKED_ACTIVE_DATES_SCOPE_CANCELLED, []);
  }, [resolveAndClose]);

  const boxTitle = getBoxTitle(request?.contentBox);
  const allLinkedLabel = resolvedClassIds
    .map((classId) => formatClassLabel(classById.get(classId), classId))
    .join(", ");
  const actionLabel = request?.actionLabel || "change active dates";

  return (
    <Modal
      isOpen={Boolean(request)}
      toggle={cancel}
      centered
      fade={false}
      className="linked-box-delete-modal"
      zIndex={PRESENT_BLOCKING_ALERT_Z_INDEX + 1}
    >
      <ModalHeader toggle={cancel} className="automation-sequence-step-modal-header">
        <div className="automation-sequence-detail-hero automation-sequence-detail-hero--flat">
          <div className="automation-sequence-detail-icon">
            <i className="fa fa-calendar" aria-hidden="true" />
          </div>
          <div>
            <div className="automation-sequence-detail-title">Linked Box Active Dates</div>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="linked-box-delete-modal-body">
        <div className="linked-box-delete-intro">
          <div className="linked-box-delete-box-title">{boxTitle}</div>
          <p className="linked-box-delete-box-copy">
            This box is linked to multiple classes. Choose where to {actionLabel}.
          </p>
        </div>

        {error ? (
          <div className="copy-box-to-classes-modal-error" role="alert">
            {error}
          </div>
        ) : null}

        {isLoadingClasses ? (
          <div className="linked-box-delete-loading">
            <Spinner size="sm" /> Loading linked classes...
          </div>
        ) : (
          <div className="linked-box-delete-choice-list" role="group" aria-label="Choose active dates scope">
            <button
              type="button"
              className="linked-box-delete-choice"
              onClick={() => resolveAndClose(LINKED_ACTIVE_DATES_SCOPE_ALL, resolvedClassIds)}
            >
              <span className="linked-box-delete-choice-icon is-neutral" aria-hidden="true">
                <i className="fa fa-link" />
              </span>
              <span className="linked-box-delete-choice-copy">
                <span className="linked-box-delete-choice-title">Change in all linked classes</span>
                <span className="linked-box-delete-choice-subtitle">
                  Update active dates for every linked copy{allLinkedLabel ? `: ${allLinkedLabel}` : "."}
                </span>
              </span>
            </button>
            <button
              type="button"
              className="linked-box-delete-choice"
              onClick={() => resolveAndClose(LINKED_ACTIVE_DATES_SCOPE_CURRENT, [sourceClassId])}
            >
              <span className="linked-box-delete-choice-icon is-neutral" aria-hidden="true">
                <i className="fa fa-calendar-check-o" />
              </span>
              <span className="linked-box-delete-choice-copy">
                <span className="linked-box-delete-choice-title">Change in this class only</span>
                <span className="linked-box-delete-choice-subtitle">
                  Keep the box linked, but let this class use its own active dates.
                </span>
              </span>
            </button>
          </div>
        )}
      </ModalBody>
      <ModalFooter className="linked-box-delete-modal-footer">
        <Button type="button" className="btn design-system-secondary-outline" onClick={cancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default LinkedActiveDatesChooserModal;
